import { useState } from 'react'
import { Trans } from 'react-i18next'

import {
  COND_FEATURE_FLAG_ALL,
  ConditionBooleanOperator,
  ConditionExpression,
  ConditionFeatureFlag,
  ConditionNumericOperator,
  ConditionStatementCustomText,
  ConditionTextOperator,
  ConditionValue,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultConditionOperator,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_INPUT_RHS,
} from '@makyoapp/makyo-common'
import { ChevronDown, Plus16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey } from '@/shared/builder/types'
import { getCurrentFormFieldColumns } from '@/shared/form-utils'
import { Option } from '@/shared/types'
import { filterComponentInBody } from '@/utils/builder/app-builder'

import { ConditionOperatorSelectV2 } from './ConditionOperatorSelectV2'
import { ConditionRepeaterContext, useConditionRepeaterContext } from './ConditionRepeaterContext'
import { ConditionSelectV2 } from './ConditionSelectV2'

function ConditionItem({
  index,
  totalConditions,
  actions,
}: {
  index: number
  totalConditions: number
  actions?: Option[]
}) {
  const { id, onChange, value } = useConditionRepeaterContext()
  const cond = value.conditions[index]

  const handleOperatorChange = (opt: DefaultConditionOperator) => {
    const nextCond = [...value.conditions]
    const currentCondition = nextCond[index]
    const currentOp = currentCondition.operator

    // If the operator is the same, do nothing
    if (currentOp === opt) {
      return
    }

    // Check operator types to determine if we can preserve values
    const isCurrentRange = [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(currentOp)
    const isNewRange = [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(opt)
    const isCurrentDate = [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS].includes(currentOp)
    const isNewDate = [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS].includes(opt)
    const currentNeedsRhs =
      OPERATOR_WITH_INPUT_RHS.includes(currentOp) ||
      [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
        currentOp
      )
    const newNeedsRhs =
      OPERATOR_WITH_INPUT_RHS.includes(opt) ||
      [...DATE_OPERATOR_WITH_RHS, ...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(opt)

    let nextRhs = currentCondition.rhs
    let nextRhsEnd = undefined

    // Handle different transition scenarios
    if (!newNeedsRhs) {
      // New operator doesn't need RHS - clear it
      nextRhs = undefined
    } else if (!currentNeedsRhs) {
      // Previous operator didn't need RHS, but new one does - create appropriate default
      if (isNewDate) {
        nextRhs = {
          flag: ConditionFeatureFlag.CustomText,
          value: new Date().toISOString(),
        } as ConditionStatementCustomText
      } else {
        nextRhs = {
          flag: ConditionFeatureFlag.CustomText,
          value: '',
        } as ConditionStatementCustomText
      }
    } else {
      // Both operators need RHS - try to preserve value

      if (isCurrentRange && !isNewRange) {
        // Switching from range to single: keep the start value
        nextRhs = currentCondition.rhs
      } else if (!isCurrentRange && isNewRange) {
        // Switching from single to range: duplicate the value for both start and end
        nextRhs = currentCondition.rhs
        nextRhsEnd = currentCondition.rhs ? { ...currentCondition.rhs } : undefined
      } else if (isCurrentRange && isNewRange) {
        // Both are range operators: preserve both values
        nextRhs = currentCondition.rhs
        nextRhsEnd = (currentCondition as any).endValue
          ? ({
              flag: ConditionFeatureFlag.CustomText,
              value: (currentCondition as any).endValue,
            } as ConditionStatementCustomText)
          : undefined
      } else if (isCurrentDate !== isNewDate) {
        // Switching between date and non-date: need to reset for type safety
        if (isNewDate) {
          nextRhs = {
            flag: ConditionFeatureFlag.CustomText,
            value: new Date().toISOString(),
          } as ConditionStatementCustomText
        } else {
          // Try to preserve the value if it's not a date-specific format
          nextRhs = {
            flag: ConditionFeatureFlag.CustomText,
            // @ts-ignore
            value: currentCondition.rhs?.value || '',
          } as ConditionStatementCustomText
        }
      } else {
        // Same type of operator (both single, both need RHS) - preserve value
        nextRhs = currentCondition.rhs
      }
    }

    // Handle range end value for new range operators
    if (isNewRange && !nextRhsEnd) {
      if (isNewDate) {
        nextRhsEnd = {
          flag: ConditionFeatureFlag.CustomText,
          value: new Date().toISOString(),
        } as ConditionStatementCustomText
      } else {
        nextRhsEnd = {
          flag: ConditionFeatureFlag.CustomText,
          // @ts-ignore
          value: currentCondition.rhs?.value || '',
        } as ConditionStatementCustomText
      }
    }

    nextCond[index] = {
      ...nextCond[index],
      operator: opt,
      rhs: nextRhs,
      // Add endValue for range operators
      ...(isNewRange ? { endValue: nextRhsEnd } : {}),
    }

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }

  const handleRemoveCondition = () => {
    const nextCond = [...value.conditions]
    nextCond.splice(index, 1)

    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: nextCond,
      })
    }
  }

  const shouldRenderRHS =
    OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
    DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
    [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(cond.operator)
  const shouldRenderRange = [...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
    cond.operator as ConditionNumericOperator
  )

  const gridColumn = shouldRenderRHS && !shouldRenderRange ? '1fr 1fr 1fr' : '1fr 1fr'

  return (
    <div className='flex items-center gap-2 w-full flex-col bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700'>
      <div className='flex gap-2 items-center justify-between w-full'>
        <div className='flex gap-3 items-center'>
          <div className='text-sm font-medium text-gray-700 dark:text-gray-400'>Condition {index + 1}</div>
          <span
            className={`px-4 py-1.5 bg-gray-200 dark:bg-slate-900 text-gray-700 dark:text-gray-300 font-bold text-sm rounded-md shadow-sm ${index === totalConditions - 1 ? 'invisible' : ''}`}
          >
            {value.isAny ? 'OR' : 'AND'}
          </span>
        </div>
        {/* Remove Button */}
        <button onClick={handleRemoveCondition} className='cursor-pointer flex items-center justify-center self-center'>
          <MaterialIcon icon={'close'} size={18} />
        </button>
      </div>
      <div className='flex-1 grid gap-2 w-full' style={{ gridTemplateColumns: gridColumn }}>
        {/* Left Hand Side - Workflow Condition Selection */}
        <div className='flex-1'>
          <ConditionSelectV2 id={`${id}-${index}-lhs`} index={index} placeholder='Select condition' actions={actions} />
        </div>

        {/* Operator Selection */}
        <div className='min-w-[140px]'>
          <ConditionOperatorSelectV2
            id={`${index}-if-else-conditions-op`}
            index={index}
            selected={cond.operator}
            value={value}
            data-operator={'operator'}
            onChange={handleOperatorChange}
          />
        </div>

        {/* Right Hand Side - Workflow Condition Selection or Custom Value */}
        {shouldRenderRHS && (
          <div
            className='flex-1 flex-col flex gap-2'
            style={{
              gridColumn: shouldRenderRange ? 'span 2 / span 2' : 'span 1 / span 1',
            }}
          >
            {/* Only render ConditionSelectV2 when it makes sense for the operator */}
            {OPERATOR_WITH_INPUT_RHS.includes(cond.operator) &&
              ![...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(cond.operator) && (
                <ConditionSelectV2
                  id={`${id}-${index}-rhs`}
                  placeholder='Value'
                  isRhs={true}
                  index={index}
                  actions={actions}
                />
              )}

            {/* Date/Time Input */}
            {DATE_OPERATOR_WITH_RHS.includes(cond.operator) && (
              <ConditionSelectV2
                id={id ? `${id}-${index}-rhs-date` : undefined}
                isRhs={true}
                index={index}
                isDate={true}
                actions={actions}
              />
            )}

            {/* Numeric Range Input */}
            {[...DATE_OPERATOR_WITH_RANGE_RHS, ...NUMERIC_OPERATOR_WITH_RANGE_RHS].includes(
              cond.operator as ConditionNumericOperator
            ) && (
              <div className='flex gap-2 flex flex-col'>
                <div className=''>
                  <div className='uppercase text-m-ss'>
                    <Trans>Lower Bound</Trans>
                  </div>
                  <ConditionSelectV2
                    key={`${id}-${index}-rhs-start`}
                    id={id ? `${id}-${index}-rhs-start` : undefined}
                    isRhs={true}
                    index={index}
                    isDate={true}
                  />
                </div>
                <div className=''>
                  <div className='uppercase text-m-ss'>
                    <Trans>Upper Bound</Trans>
                  </div>
                  <ConditionSelectV2
                    key={`${id}-${index}-rhs-end`}
                    id={id ? `${id}-${index}-rhs-end` : undefined}
                    isRhs={true}
                    index={index}
                    isEnd
                    isDate={true}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface ConditionRepeaterProps {
  id?: string
  value: ConditionValue
  onChange?: (nextValue: ConditionValue) => void
  featureFlags?: ConditionFeatureFlag
  parentFormId?: string
  actions?: Option[]
}

export function ConditionRepeaterHorizontal({
  id,
  value,
  onChange,
  featureFlags = COND_FEATURE_FLAG_ALL,
  parentFormId,
  actions,
}: ConditionRepeaterProps) {
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()
  const [showLogicDropdown, setShowLogicDropdown] = useState(false)

  const getDefaultCondition = (): ConditionExpression[] => {
    // form elements: generate default form condition
    if (parentFormId) {
      const containerSettings = activeLayerClient?.settings._BodySettings
      const form = activeLayerClient?.componentsMap.get(parentFormId as string)

      if (form) {
        const formChildren = filterComponentInBody(form._Body, (el) => ['form', 'upload'].includes(el._GroupID))
        const formSettings = form?.settings
        const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
        const table = tables.find((t) => t.id === dataSource)

        if (containerSettings && formChildren && table) {
          const columns = getCurrentFormFieldColumns(containerSettings, formChildren)
          const firstColId = columns[0]

          return [
            {
              lhs: { flag: ConditionFeatureFlag.FormData, formId: parentFormId, col: firstColId },
              operator: ConditionTextOperator.IsNotEmpty,
            },
          ]
        }
      }
    }

    // other elements: generate default user logged in condition
    return [
      {
        lhs: { flag: ConditionFeatureFlag.UserLoggedIn },
        operator: ConditionBooleanOperator.IsFalse,
      },
    ]
  }

  const handleAddCondition = () => {
    if (onChange) {
      onChange({
        isAny: value.isAny,
        conditions: [...(value.conditions || []), ...getDefaultCondition()],
      })
    }
  }

  return (
    <ConditionRepeaterContext.Provider value={{ id, value, onChange, featureFlags, parentFormId }}>
      {/* Logic Mode Selector */}
      <div>
        <div className='mb-6 relative'>
          <button
            onClick={() => setShowLogicDropdown(!showLogicDropdown)}
            className='flex items-center justify-between w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-3 rounded border border-gray-300 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-500 transition-colors'
          >
            <span>{value?.isAny ? 'At least one condition must be met (OR)' : 'All conditions must be met (AND)'}</span>
            <ChevronDown className='h-4 w-4' />
          </button>

          {showLogicDropdown && (
            <div className='absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-10'>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: false,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  !value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>All conditions must be met</Trans>
              </button>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: true,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>At least one condition must be met</Trans>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className='gap-3 flex flex-col'>
        {value?.conditions?.map((_, index) => (
          <ConditionItem totalConditions={value?.conditions?.length} key={index} index={index} actions={actions} />
        ))}
        <div className='flex justify-end'>
          <Button onClick={handleAddCondition} variant={ButtonVariant.Outlined} className='cursor-pointer'>
            <Plus16 className='h-4 w-4' />
            <Trans>Add Condition</Trans>
          </Button>
        </div>
      </div>
    </ConditionRepeaterContext.Provider>
  )
}
